import React, { useState, useMemo, useEffect } from 'react';
import { useUser } from '../../../contexts';
import ViewSingleData from '../masters/components/ViewSingleData';
import { getReportLedger, getReportOutstandingDetail } from '../../../redux/actions/reports';
import { getSingleCustomer } from '../../../redux/actions/customer';
import { useDispatch, useSelector } from 'react-redux';
import { X, Wallet, Search, Info, FileQuestion, ArrowUpDown } from 'lucide-react';
import { useColorMode } from '@chakra-ui/react';
import Loader from '../default/Loader';

const PartyForm = ({ data, onClose, type }) => {
   const [searchTerm, setSearchTerm] = useState('');
   const [sortConfig, setSortConfig] = useState({
      key: 'name',
      direction: 'asc',
   });
   const { user } = useUser();
   const dispatch = useDispatch();
   const [isOpenSingleView, setIsOpenSingleView] = useState(false);
   const [dynamicArray, setDynamicArray] = useState([]);
   const Ledger = useSelector((state) => state.reports.ledger || []);
   const OS = useSelector((state) => state.reports.outstanding || []);
   const singleEntity = useSelector((state) => state?.customer?.single || {});
   const companyId = user?.loginhistory?.companyId;
   const accountRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.AllAccount
      : null;
   const singleEntityWithLedger = {
      ...singleEntity,
      ledger: Ledger,
      os: OS,
   };

   const [isLoading, setIsLoading] = useState(true);
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   useEffect(() => {
      const timer = setTimeout(() => {
         setIsLoading(false);
      }, 500);

      return () => clearTimeout(timer);
   }, []);

   useEffect(() => {
      const handleEscKey = (event) => {
         if (event.keyCode === 27 || event.key === 'Escape') {
            onClose();
         }
      };

      document.addEventListener('keydown', handleEscKey);
      return () => {
         document.removeEventListener('keydown', handleEscKey);
      };
   }, [onClose]);

   const filteredByType = useMemo(() => {
      if (!Array.isArray(data) || data.length === 0) return [];
      if (type === 'Payables') {
         const payables = data.filter((account) => (account.amt || 0) > 0);
         return payables;
      } else {
         const receivables = data.filter((account) => (account.amt || 0) < 0);
         return receivables;
      }
   }, [data, type]);

   const filteredData = useMemo(() => {
      if (!Array.isArray(filteredByType) || filteredByType.length === 0) return [];
      const term = searchTerm.trim().toLowerCase();
      if (!term) return filteredByType;
      return filteredByType.filter(
         (item) =>
            String(item.name ?? '')
               .toLowerCase()
               .includes(term) ||
            String(item.mobile ?? '')
               .toLowerCase()
               .includes(term) ||
            String(item.gstRegNo ?? '')
               .toLowerCase()
               .includes(term) ||
            String(item.panNo ?? '')
               .toLowerCase()
               .includes(term) ||
            String(item.ownerName ?? '')
               .toLowerCase()
               .includes(term) ||
            String(Math.abs(item.amt ?? '')).includes(term),
      );
   }, [filteredByType, searchTerm]);

   const sortedData = useMemo(() => {
      if (!filteredData || filteredData.length === 0) return [];

      return [...filteredData].sort((a, b) => {
         if (sortConfig.key === 'name') {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            if (nameA < nameB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (nameA > nameB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
         } else if (sortConfig.key === 'amt') {
            const amtA = a.amt || 0;
            const amtB = b.amt || 0;
            return sortConfig.direction === 'asc' ? amtA - amtB : amtB - amtA;
         }
         return 0;
      });
   }, [filteredData, sortConfig]);

   const totalBalance = useMemo(() => {
      if (!filteredData.length) return 0;
      return filteredData.reduce((acc, cur) => acc + (cur.amt || 0), 0);
   }, [filteredData]);

   const formatBalance = (amount) => {
      const num = Math.abs(Number(amount || 0)).toFixed(2);
      const [integerPart, decimalPart] = num.split('.');

      return (
         <>
            {integerPart}
            <span style={{ fontSize: '0.75em' }}>.{decimalPart}</span>
         </>
      );
   };

   if (isLoading) {
      return <Loader />;
   }

   const handleSort = (key) => {
      setSortConfig((prevConfig) => ({
         key,
         direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
      }));
   };

   const handleSingleView = async (data) => {
      const accountbyid = {
         id: Number(data.id),
         compId: String(user?.loginhistory?.companyId),
      };
      const today = new Date().toISOString().split('T')[0];
      await dispatch(
         getReportOutstandingDetail({
            accIds: data.id,
            fromDate: '2010-04-01',
            toDate: today,
            companyIds: companyId,
            isPurchaseOs: data?.glgroupcode === 'E' ? 1 : 0,
         }),
      );
      await dispatch(getSingleCustomer(accountbyid));
      if (accountbyid) {
         await dispatch(
            getReportLedger({
               fromDate: '2010-04-01',
               toDate: today,
               companyIds: companyId.toString(),
               accIds: accountbyid.id.toString(),
            }),
         );
      }

      var dynamicArray = [];
      dynamicArray = [
         { label: 'Name', value: data.name || '-' },
         { label: 'Email', value: data.email || '-' },
         { label: 'Mobile', value: data.mobile || '-' },
         {
            label: 'Address',
            value: data.accountaddress?.[0]?.address1 || '-',
         },
         { label: 'GSTIN', value: data.accountaddress?.[0]?.gst || '-' },
         { label: 'City', value: data.accountaddress?.[0]?.city || '-' },
         { label: 'State', value: data.accountaddress?.[0]?.state || '-' },
         {
            label: 'Country',
            value: data.accountaddress?.[0]?.country || '-',
         },
         {
            label: 'Pincode',
            value: data.accountaddress?.[0]?.pincode || '-',
         },
      ];
      setDynamicArray(dynamicArray);

      setIsOpenSingleView(true);
   };

   // ✅ Helper function to get row styles based on status
   const getRowStyles = (status, isDark) => {
      if (status === false) {
         return isDark
            ? 'bg-red-900/10 border-l-4 border-l-red-500 text-red-300'
            : 'bg-red-50/50 border-l-4 border-l-red-400 text-red-800';
      }
      return '';
   };

   return (
      <div
         className={`rounded-2xl shadow-2xl border-2 w-full max-w-2xl max-h-[85vh] flex flex-col transition-all duration-300 ${isDark
               ? 'bg-gray-800/95 backdrop-blur-xl border-gray-700/50'
               : 'bg-white/95 backdrop-blur-xl border-gray-200/50'
            }`}>
         {/* Header */}
         <div
            className={`px-6 py-5 border-b-2 rounded-t-xl flex items-center justify-between ${isDark
                  ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/80 to-gray-800/60'
                  : 'border-gray-200/50 bg-gradient-to-r from-blue-50/50 to-indigo-50/30'
               }`}>
            <div className="flex items-center gap-4">
               <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${isDark
                        ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/40 border-2 border-blue-700/50 shadow-lg shadow-blue-900/30'
                        : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200/50 shadow-md shadow-blue-200/50'
                     }`}>
                  <Wallet className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} strokeWidth={1.5} />
               </div>
               <div>
                  <h2 className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                     {type} Information
                  </h2>
                  <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                     View and search {type.toLowerCase()} balances
                  </p>
               </div>
            </div>
            <button
               className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-300 hover:scale-110 ${isDark
                     ? 'hover:bg-gray-700/70 border-2 border-gray-700'
                     : 'hover:bg-gray-100 border-2 border-gray-200'
                  }`}
               onClick={onClose}>
               <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} strokeWidth={1.5} />
            </button>
         </div>

         {/* Search Bar */}
         <div className={`px-6 pt-5 pb-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50/50'}`}>
            <div className="relative group">
               <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-all duration-300 ${searchTerm
                        ? isDark
                           ? 'text-blue-400'
                           : 'text-blue-600'
                        : isDark
                           ? 'text-gray-500'
                           : 'text-gray-400'
                     }`}>
                  <Search className="w-5 h-5" strokeWidth={1.5} />
               </div>
               <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search by account name or balance...`}
                  className={`w-full pl-12 pr-4 py-3 text-sm rounded-xl transition-all duration-300 ${isDark
                        ? 'bg-gray-700/70 border-2 border-gray-600/80 text-white placeholder-gray-400 focus:border-blue-500/70 focus:bg-gray-700 focus:ring-4 focus:ring-blue-900/30'
                        : 'bg-white border-2 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-400/70 focus:ring-4 focus:ring-blue-200/50 shadow-sm'
                     } focus:outline-none`}
               />
            </div>
         </div>

         {/* Table */}
         <div className="flex-1 overflow-hidden px-6 pb-5">
            <div
               className={`overflow-y-auto rounded-xl border-2 ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'}`}
               style={{ maxHeight: '50vh' }}>
               {sortedData.length > 0 ? (
                  <table className="w-full">
                     <thead
                        className={`sticky top-0 z-10 ${isDark
                              ? 'bg-gradient-to-r from-gray-800 to-gray-800/95 backdrop-blur-sm'
                              : 'bg-gradient-to-r from-gray-50 to-blue-50/30 backdrop-blur-sm'
                           }`}>
                        <tr className="text-left">
                           <th
                              className={`px-4 py-4 text-xs font-bold uppercase tracking-wider border-b-2 w-16 ${isDark ? 'text-gray-400 border-gray-700/50' : 'text-gray-600 border-gray-200/50'
                                 }`}>
                              #
                           </th>
                           <th
                              onClick={() => handleSort('name')}
                              className={`px-4 py-4 text-xs font-bold uppercase tracking-wider border-b-2 cursor-pointer select-none hover:bg-opacity-50 transition-colors ${isDark
                                    ? 'text-gray-400 border-gray-700/50 hover:bg-gray-700/30'
                                    : 'text-gray-600 border-gray-200/50 hover:bg-gray-100/50'
                                 }`}>
                              <div className="flex items-center gap-2">
                                 Account Name
                                 <ArrowUpDown
                                    className={`w-4 h-4 transition-transform duration-300 ${sortConfig.key === 'name'
                                          ? sortConfig.direction === 'asc'
                                             ? 'rotate-180 text-blue-500'
                                             : 'text-blue-500'
                                          : isDark
                                             ? 'text-gray-500'
                                             : 'text-gray-400'
                                       }`}
                                    strokeWidth={1.5}
                                 />
                              </div>
                           </th>
                           <th
                              className={`px-4 py-4 text-xs font-bold uppercase tracking-wider border-b-2 ${isDark ? 'text-gray-400 border-gray-700/50' : 'text-gray-600 border-gray-200/50'
                                 }`}>
                              Mobile No
                           </th>
                           <th
                              onClick={() => handleSort('amt')}
                              className={`px-4 py-4 text-xs font-bold uppercase tracking-wider border-b-2 text-right cursor-pointer select-none hover:bg-opacity-50 transition-colors ${isDark
                                    ? 'text-gray-400 border-gray-700/50 hover:bg-gray-700/30'
                                    : 'text-gray-600 border-gray-200/50 hover:bg-gray-100/50'
                                 }`}>
                              <div className="flex items-center justify-end gap-2">
                                 Balance
                                 <ArrowUpDown
                                    className={`w-4 h-4 transition-transform duration-300 ${sortConfig.key === 'amt'
                                          ? sortConfig.direction === 'asc'
                                             ? 'rotate-180 text-blue-500'
                                             : 'text-blue-500'
                                          : isDark
                                             ? 'text-gray-500'
                                             : 'text-gray-400'
                                       }`}
                                    strokeWidth={1.5}
                                 />
                              </div>
                           </th>
                        </tr>
                     </thead>
                     <tbody className={`divide-y ${isDark ? 'divide-gray-700/50' : 'divide-gray-100'}`}>
                        {sortedData.map((acc, i) => {
                           const isInactive = acc.status === false;
                           const rowStyles = getRowStyles(acc.status, isDark);

                           return (
                              <tr
                                 key={acc.id || i}
                                 className={`group cursor-pointer transition-all duration-200 ${rowStyles} ${!isInactive
                                       ? isDark
                                          ? 'hover:bg-gradient-to-r hover:from-blue-900/20 hover:to-blue-800/10'
                                          : 'hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/30'
                                       : isDark
                                          ? 'hover:bg-red-900/20'
                                          : 'hover:bg-red-100/50'
                                    }`}
                                 onClick={() => handleSingleView(acc)}>
                                 <td
                                    className={`px-4 py-4 text-sm ${isInactive
                                          ? isDark
                                             ? 'text-red-300'
                                             : 'text-red-700'
                                          : isDark
                                             ? 'text-gray-400'
                                             : 'text-gray-500'
                                       }`}>
                                    {i + 1}
                                 </td>
                                 <td
                                    className={`px-4 py-4 text-sm font-semibold ${isInactive
                                          ? isDark
                                             ? 'text-red-300'
                                             : 'text-red-800'
                                          : isDark
                                             ? 'text-gray-200 group-hover:text-blue-300'
                                             : 'text-gray-900 group-hover:text-blue-700'
                                       } transition-colors`}>
                                    {acc.name}
                                    {isInactive && (
                                       <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-200 text-red-800">
                                          Inactive
                                       </span>
                                    )}
                                 </td>
                                 <td
                                    className={`px-4 py-4 text-sm font-medium ${isInactive
                                          ? isDark
                                             ? 'text-red-300'
                                             : 'text-red-700'
                                          : isDark
                                             ? 'text-gray-300'
                                             : 'text-gray-700'
                                       }`}>
                                    {acc.mobile && acc.mobile !== '' ? acc.mobile : '-'}
                                 </td>
                                 <td className="px-4 py-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                       <span
                                          className={`text-sm font-bold ${isInactive
                                                ? isDark
                                                   ? 'text-red-300'
                                                   : 'text-red-700'
                                                : acc.amt < 0
                                                   ? isDark
                                                      ? 'text-green-400'
                                                      : 'text-green-600'
                                                   : isDark
                                                      ? 'text-red-400'
                                                      : 'text-red-600'
                                             }`}>
                                          {formatBalance(acc.amt)}
                                       </span>
                                       <span
                                          className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border-2 transition-all duration-200 ${isInactive
                                                ? isDark
                                                   ? 'bg-red-900/30 text-red-300 border-red-700/50'
                                                   : 'bg-red-100 text-red-700 border-red-300'
                                                : acc.amt < 0
                                                   ? isDark
                                                      ? 'bg-green-900/30 text-green-300 border-green-700/50 group-hover:shadow-lg group-hover:shadow-green-900/30'
                                                      : 'bg-green-50 text-green-700 border-green-300/50 group-hover:shadow-md group-hover:shadow-green-200/50'
                                                   : isDark
                                                      ? 'bg-red-900/30 text-red-300 border-red-700/50 group-hover:shadow-lg group-hover:shadow-red-900/30'
                                                      : 'bg-red-50 text-red-700 border-red-300/50 group-hover:shadow-md group-hover:shadow-red-200/50'
                                             }`}>
                                          {acc.amt < 0 ? 'DB' : 'CR'}
                                       </span>
                                    </div>
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                  </table>
               ) : (
                  <div
                     className={`flex flex-col items-center justify-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                     <div
                        className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${isDark ? 'bg-gray-700/50 border-2 border-gray-600' : 'bg-gray-100 border-2 border-gray-200'
                           }`}>
                        <FileQuestion className={`w-8 h-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                     </div>
                     <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        No {type.toLowerCase()} accounts found.
                     </p>
                  </div>
               )}
            </div>
         </div>

         {/* Footer */}
         <div
            className={`px-6 py-5 border-t-2 rounded-b-2xl ${isDark
                  ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/80 to-gray-800/60'
                  : 'border-gray-200/50 bg-gradient-to-r from-gray-50 to-blue-50/30'
               }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
               <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Info className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-medium">
                     Showing{' '}
                     <span className={`font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        {filteredData.length}
                     </span>{' '}
                     of {filteredByType.length} {type.toLowerCase()} accounts
                  </span>
               </div>

               <div className="flex items-center gap-4">
                  <div
                     className={`flex items-center gap-2 text-sm px-4 py-2 rounded-xl border-2 ${isDark ? 'bg-gray-700/50 border-gray-600/50' : 'bg-white border-gray-200/50 shadow-sm'
                        }`}>
                     <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total {type}:</span>
                     <span
                        className={`font-bold text-base ${totalBalance < 0
                              ? isDark
                                 ? 'text-green-400'
                                 : 'text-green-600'
                              : isDark
                                 ? 'text-red-400'
                                 : 'text-red-600'
                           }`}>
                        {formatBalance(totalBalance)} {totalBalance < 0 ? 'DB' : 'CR'}
                     </span>
                  </div>

                  <button
                     className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-500/50 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 border-2 border-blue-500"
                     onClick={onClose}>
                     Close
                  </button>
               </div>
            </div>
         </div>

         <ViewSingleData
            singleEntityRights={accountRight}
            isOpenSingleView={isOpenSingleView}
            onClose={() => setIsOpenSingleView(false)}
            entityType="Account"
            singleEntity={singleEntityWithLedger}
            dynamicArray={dynamicArray}
            formType="Account"
         />
      </div>
   );
};

export default PartyForm;
